import { useCallback, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { type Optional } from '@direct-frontend/stdlib';
import { type PickupPoint } from '@/shared/api/pickupPoint';

type SelfPickupFilters = Partial<{
    canTakeAllItems: boolean;
}>;

export type SelfPickupFilterKeys = keyof SelfPickupFilters;

interface UseSelfPickupFiltersParams {
    points: Array<PickupPoint>;
    allItemsCount: Optional<number>;
}

export const useSelfPickupFilters = ({ points, allItemsCount }: UseSelfPickupFiltersParams) => {
    const [selfPickupFilters, setSelfPickupFilters] = useState<SelfPickupFilters>({});

    const changeSelfPickupFilters = useCallback(
        <T extends SelfPickupFilterKeys>(filter: T, value: SelfPickupFilters[T]) => {
            const { [filter]: currentValue, ...other } = selfPickupFilters;

            if ((currentValue && currentValue !== value) || !currentValue) {
                if (!value && isEmpty(value)) {
                    setSelfPickupFilters(other);
                } else {
                    setSelfPickupFilters({ ...other, [filter]: value });
                }
            } else {
                setSelfPickupFilters(other);
            }
        },
        [selfPickupFilters]
    );

    const filteredPoints = useMemo(() => {
        if (isEmpty(selfPickupFilters)) {
            return points;
        }

        return points.filter((point) => {
            if (selfPickupFilters.canTakeAllItems) {
                const { itemsCount } = point;

                return itemsCount && allItemsCount && itemsCount === allItemsCount;
            }

            return true;
        });
    }, [allItemsCount, points, selfPickupFilters]);

    return {
        selfPickupFilters,
        changeSelfPickupFilters,
        points: filteredPoints,
    };
};
